import { ReactNode, useEffect, useMemo, useRef } from 'react';
import {
  dedupeKeepingLastOccurrence,
  keysOf,
  normalizeStack,
  PanelLayer,
} from './_panel-layers';

type UseSideShellPanelsParams = {
  panels?: Record<string, ReactNode>;
  activePanel?: string | string[];
  onActivePanelChange?: (activePanel: string | string[] | undefined) => void;
  isCollapsed: boolean;
  // Called to reveal the side section when navigation changes it while collapsed.
  onAutoOpen: () => void;
};

// Derive the rendered stack from the selector: keep only keys present in
// `panels`, deduped to each key's last occurrence.
const usePanelLayers = (
  panels: Record<string, ReactNode> | undefined,
  activePanel: string | string[] | undefined
): PanelLayer[] =>
  useMemo(() => {
    if (!panels) {
      return [];
    }
    const keys = dedupeKeepingLastOccurrence(
      normalizeStack(activePanel).filter((key) => panels[key] !== undefined)
    );
    return keys.map((key) => ({ key, node: panels[key] }));
  }, [panels, activePanel]);

// Reveal the side section when navigation changes it while collapsed — the
// newly activated panel would otherwise be hidden behind the collapsed edge.
const useAutoOpenOnPanelChange = (
  layers: PanelLayer[],
  isCollapsed: boolean,
  onAutoOpen: () => void
) => {
  const activePanelSig = keysOf(layers).join(' ');
  const prevActivePanelSigRef = useRef(activePanelSig);

  useEffect(() => {
    if (prevActivePanelSigRef.current === activePanelSig) {
      return;
    }

    prevActivePanelSigRef.current = activePanelSig;

    if (isCollapsed) {
      onAutoOpen();
    }

    // `isCollapsed`/`onAutoOpen` are read fresh; the ref guard ensures this only
    // acts on an actual `activePanel` change, not on collapse toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanelSig]);
};

/**
 * Composes the SideShell panel-stack logic: the rendered layers, the
 * back-navigation handler exposed through context, and auto-opening the side
 * section when the active panel changes while collapsed.
 */
export const useSideShellPanels = ({
  panels,
  activePanel,
  onActivePanelChange,
  isCollapsed,
  onAutoOpen,
}: UseSideShellPanelsParams) => {
  const layers = usePanelLayers(panels, activePanel);

  const contextValue = useMemo(
    () => ({
      goBack: () => {
        const next = layers.slice(0, -1).map((layer) => layer.key);
        onActivePanelChange?.(next.length > 0 ? next : undefined);
      },
    }),
    [layers, onActivePanelChange]
  );

  useAutoOpenOnPanelChange(layers, isCollapsed, onAutoOpen);

  return { layers, contextValue };
};
